import { useEffect, useState } from 'react';
import { useNavStore } from '../store/useNavStore';
import { BOUNDARY_FEEDBACK_MS } from '../lib/const';

/**
 * NAV-07 scaffold — warm-vignette overlay that appears when the walk rig signals
 * the player has touched the outer boundary.
 *
 * This component is a PURE CONSUMER: it subscribes to `useNavStore.boundaryHit`
 * and self-times its visibility for BOUNDARY_FEEDBACK_MS (1500ms). Plan 03's
 * WalkRig is the producer — it writes `boundaryHit=true` when the camera has
 * exited `outerBoundary`. Because this plan cannot fully test the flow (no
 * WalkRig yet), verification here is structural: grep confirms the subscription.
 *
 * AESTHETIC-BRIEF Rule 3 + 7: warm vignette (not neutral gray), period-quiet
 * serif ("The lakeshore ends here — turn back").
 */
export function BoundaryFeedback() {
  const boundaryHit = useNavStore((s) => s.boundaryHit);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!boundaryHit) return;
    setVisible(true);
    const t = setTimeout(() => setVisible(false), BOUNDARY_FEEDBACK_MS);
    return () => clearTimeout(t);
  }, [boundaryHit]);

  if (!visible) return null;
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        inset: 0,
        pointerEvents: 'none',
        background:
          'radial-gradient(circle at center, transparent 40%, rgba(80, 40, 20, 0.5) 100%)',
        transition: 'opacity 400ms',
        zIndex: 10,
      }}
    >
      <div
        style={{
          position: 'absolute',
          bottom: '22%',
          width: '100%',
          textAlign: 'center',
          fontFamily:
            '"Goudy Old Style", "Cormorant Garamond", Georgia, serif',
          color: '#FDF6EE',
          fontSize: '1.25rem',
          textShadow: '0 2px 4px rgba(0,0,0,0.5)',
        }}
      >
        The lakeshore ends here — turn back
      </div>
    </div>
  );
}
